import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { normalizeFillGapAnswer as normalize } from './FillGapsElement';

// Table-fill exercise. Each body cell is either:
//   - given: text is shown to the student as-is;
//   - answer: text is the correct answer (alternatives split by `|`) and the
//     student types into an input;
//   - empty (not given, no text): a blank decorative cell, not graded.
// The header row (when enabled) is always shown.

export const tableFillCellKey = (r, c) => `${r}-${c}`;

const isHeaderCell = (content, r) => Boolean(content?.headerRow) && r === 0;

// After sanitizing for students the answer text is stripped, so `isAnswer`
// carries the "this cell is an input" flag on its own.
const isAnswerCell = (content, cell, r) => {
  if (isHeaderCell(content, r) || !cell || cell.given) return false;
  return Boolean(cell.isAnswer) || (cell.text || '').trim().length > 0;
};

const getRows = (content) => (
  Array.isArray(content?.rows) && content.rows.length > 0 ? content.rows : []
);

export const getTableFillAnswerKeys = (content) => {
  const keys = [];
  getRows(content).forEach((row, r) => {
    (row || []).forEach((cell, c) => {
      if (isAnswerCell(content, cell, r)) keys.push(tableFillCellKey(r, c));
    });
  });
  return keys;
};

const cellCorrectAnswers = (cell) => (cell?.text || '')
  .split('|')
  .map((s) => s.trim())
  .filter(Boolean);

export const computeTableFillResults = (content, answers) => {
  const rows = getRows(content);
  const perCellResults = {};
  getTableFillAnswerKeys(content).forEach((key) => {
    const [r, c] = key.split('-').map(Number);
    const correct = cellCorrectAnswers(rows[r]?.[c]);
    const userVal = normalize(answers?.[key]);
    perCellResults[key] = userVal.length > 0 && correct.some((a) => normalize(a) === userVal);
  });
  const values = Object.values(perCellResults);
  const isCorrect = values.length > 0 && values.every((v) => v === true);
  return { perCellResults, isCorrect };
};

// Strips answer text so correct answers never reach the student's browser.
export const sanitizeTableFillContent = (content) => ({
  ...content,
  rows: getRows(content).map((row, r) => (row || []).map((cell) => (
    isAnswerCell(content, cell, r) ? { given: false, isAnswer: true, text: '' } : cell
  ))),
});

const TableFillElement = ({
  element,
  isEditor = false,
  answers: externalAnswers = null,
  showResult = false,
  perCellResults = null,
  onAnswersChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  updateCell = null,
}) => {
  const { t } = useTranslation();
  const content = useMemo(() => element.content || {}, [element.content]);
  const rows = getRows(content);
  const {
    allowRetry = false,
    borderWidth = 1,
    borderColor = '#2A4D25',
    headerBg = '#2A4D25',
    headerColor = '#ffffff',
    cellBg = '#ffffff',
    textColor = '#1f2937',
    fontSize = 18,
    fontFamily = 'Inter',
    inputBgColor = '#ffffff',
    inputBorderColor = '#2A4D25',
    buttonColor = '#2A4D25',
  } = content;

  const [answers, setAnswers] = useState(externalAnswers || {});
  useEffect(() => {
    const external = externalAnswers || {};
    // Same sync rules as FillGaps: read-only viewers mirror external state;
    // an active student keeps local edits unless external was reset or this
    // is the initial async load.
    if (disabled || isEditor || showResult) {
      setAnswers(external);
      return;
    }
    setAnswers((prev) => {
      const prevEmpty = !prev || Object.keys(prev).length === 0;
      const externalEmpty = Object.keys(external).length === 0;
      if (prevEmpty && !externalEmpty) return external;
      if (externalEmpty) return {};
      return prev;
    });
  }, [externalAnswers, disabled, isEditor, showResult]);

  const answerKeys = useMemo(() => getTableFillAnswerKeys(content), [content]);
  const allFilled = answerKeys.length > 0
    && answerKeys.every((key) => (answers[key] || '').trim().length > 0);
  const allCorrect = perCellResults && Object.keys(perCellResults).length > 0
    && Object.values(perCellResults).every((v) => v === true);

  const handleInputChange = (key, value) => {
    if (disabled || showResult || isEditor) return;
    const next = { ...answers, [key]: value };
    setAnswers(next);
    onAnswersChange?.(next);
  };

  const handleCellTextChange = (rIdx, cIdx, value) => {
    if (!updateCell) return;
    const nextRows = rows.map((row, r) =>
      row.map((cell, c) => (r === rIdx && c === cIdx ? { ...cell, text: value } : cell))
    );
    updateCell(nextRows);
  };

  const handleRetry = () => {
    setAnswers({});
    onRetry?.();
  };

  // Answer cells have no inner frame — the whole cell is the input, and the
  // result tint goes on the cell itself.
  const answerCellColors = (key) => {
    const result = showResult && perCellResults ? perCellResults[key] : null;
    if (result === true) return { bg: '#dcfce7', color: '#166534' };
    if (result === false) return { bg: '#fee2e2', color: '#991b1b' };
    return { bg: inputBgColor, color: textColor };
  };

  const bareInputStyle = {
    width: '100%',
    background: 'transparent',
    color: 'inherit',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'center',
    border: 'none',
    outline: 'none',
    padding: 0,
  };

  const renderCellBody = (cell, r, c) => {
    const key = tableFillCellKey(r, c);
    const answer = isAnswerCell(content, cell, r);

    if (isEditor && updateCell) {
      return (
        <input
          type="text"
          value={cell?.text || ''}
          onChange={(e) => handleCellTextChange(r, c, e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            ...bareInputStyle,
            fontWeight: 'inherit',
            // Admin hint: answer cells show the expected answer in italic brand green.
            ...(answer ? { fontStyle: 'italic', color: inputBorderColor } : {}),
          }}
        />
      );
    }

    if (answer) {
      if (isEditor) {
        // Thumbnail / read-only editor render: expected answer in italic brand green.
        return (
          <span style={{ fontStyle: 'italic', color: inputBorderColor }}>{cell?.text || ''}</span>
        );
      }
      return (
        <input
          type="text"
          value={answers[key] || ''}
          onChange={(e) => handleInputChange(key, e.target.value)}
          disabled={disabled || showResult}
          style={{ ...bareInputStyle, fontWeight: 500 }}
        />
      );
    }

    return <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{cell?.text || ''}</span>;
  };

  return (
    <div className="w-full h-full relative" style={{ overflow: 'hidden' }}>
      <table
        className="w-full h-full"
        style={{
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontSize: `${fontSize}px`,
          color: textColor,
          fontFamily,
        }}
      >
        <tbody>
          {rows.map((row, rIdx) => {
            const isHeader = isHeaderCell(content, rIdx);
            return (
              <tr key={rIdx}>
                {row.map((cell, cIdx) => {
                  const answerColors = !isEditor && isAnswerCell(content, cell, rIdx)
                    ? answerCellColors(tableFillCellKey(rIdx, cIdx))
                    : null;
                  return (
                  <td
                    key={cIdx}
                    style={{
                      border: `${borderWidth}px solid ${borderColor}`,
                      background: isHeader ? headerBg : (answerColors ? answerColors.bg : cellBg),
                      color: isHeader ? headerColor : (answerColors ? answerColors.color : textColor),
                      transition: 'background-color 150ms',
                      fontWeight: isHeader ? 600 : 400,
                      padding: '6px 8px',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                    }}
                  >
                    {renderCellBody(cell, rIdx, cIdx)}
                  </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {!isEditor && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
          {showResult && allowRetry && !allCorrect && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={() => onCheck?.(answers)}
              disabled={!allFilled}
              className="px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: allFilled ? buttonColor : undefined }}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TableFillElement;
